import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { SCUIButton } from '../../../../atomic/atoms/SCUIButton/SCUIButton';

interface RequestNoteModalProps {
    open: boolean;
    onClose: () => void;
    requestNote: string | null;
    roleName: string;
    requestDate: string;
}

export const RequestNoteModal: React.FC<RequestNoteModalProps> = ({
    open,
    onClose,
    requestNote,
    roleName,
    requestDate,
}) => {
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #E2E8F0',
                    pb: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <DescriptionIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '1.125rem' }}>
                            {t('roleApproval.myRoleRequests.requestMessage')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                            {roleName} • {requestDate}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: '#64748B' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ py: 3 }}>
                {requestNote ? (
                    <Box
                        sx={{
                            p: 2.5,
                            backgroundColor: '#F8FAFC',
                            borderRadius: 2,
                            border: '1px solid #E2E8F0',
                            minHeight: 100,
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#334155',
                                lineHeight: 1.7,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                        >
                            {requestNote}
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            p: 3,
                            backgroundColor: '#F8FAFC',
                            borderRadius: 2,
                            border: '1px dashed #CBD5E1',
                            textAlign: 'center',
                            minHeight: 100,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#94A3B8', fontStyle: 'italic' }}>
                            {t('roleApproval.myRoleRequests.noRequestNote')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: '1px solid #E2E8F0',
                    px: 3,
                    py: 2,
                }}
            >
                <SCUIButton
                    onClick={onClose}
                    type="secondary"
                    buttonType="button"
                    sizeType="fixed"
                    size="m"
                    text={t('roleApproval.myRoleRequests.close')}
                />
            </DialogActions>
        </Dialog>
    );
};
